import {
  Alert,
  Container,
  Header,
  SpaceBetween,
  KeyValuePairs,
  Button,
  StatusIndicator,
  Box,
  Modal,
  ColumnLayout,
  Table,
  Tabs,
  Popover,
  Link,
} from '@cloudscape-design/components';
import { useParams, useSearchParams } from 'react-router';
import { useState, useEffect } from 'react';
import type { EvaluationDetails, EvaluationResult } from '@amzn/helianthus-arbiter-client';
import { EvaluationStatus, ResourceNotFoundException } from '@amzn/helianthus-arbiter-client';
import { useArbiterClient } from '../../context/ArbiterClientContext';
import { ErrorState } from '../../components/ErrorState';
import { LoadingState } from '../../components/LoadingState';
import { AuditTrailTimeline } from '../../components/AuditTrailTimeline';
import { useEvaluationAuditTrail } from '../../hooks/useEvaluationAuditTrail';
import { getStatusType, getOutcomeType, formatSubject, UNKNOWN_SUBJECT, parseRuleId, buildRuleUrl, EVALUATION_STATUS_DISPLAY } from '../../utils/evaluationHelpers';
import { ConfidenceBadge } from '../../components/ConfidenceBadge';
import { PerformanceMetricsPanel } from '../../components/PerformanceMetricsPanel';
import { AuditTrailExportButton } from '../../components/AuditTrailExportButton';
import type { EvaluationOutcome } from '../../utils/evaluationHelpers';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

function formatDateTime(date: Date | undefined): string {
  return date?.toLocaleString() ?? '--';
}

// ---------------------------------------------------------------------------
// Audit Trail Tab content
// ---------------------------------------------------------------------------

function AuditTrailTab({ evaluationId, enabled }: { evaluationId: string; enabled: boolean }): React.JSX.Element {
  const { auditTrail, loading, error, isNotFound } = useEvaluationAuditTrail(evaluationId, { enabled });

  if (!enabled) {
    return <Box textAlign="center" color="text-body-secondary" padding={{ top: 'l' }}>Select this tab to load the audit trail.</Box>;
  }

  if (loading) {
    return <LoadingState label="Loading audit trail..." />;
  }

  if (isNotFound) {
    return (
      <Alert type="info">
        Audit trail not available for this evaluation. Audit trails are generated
        for evaluations created after LookingGlass deployment.
      </Alert>
    );
  }

  if (error) {
    return (
      <ErrorState
        variant="api-error"
        title="Error loading audit trail"
        message={error.message}
      />
    );
  }

  if (!auditTrail || !auditTrail.auditEntries || auditTrail.auditEntries.length === 0) {
    return (
      <Alert type="info">
        No audit trail entries found for this evaluation.
      </Alert>
    );
  }

  return (
    <SpaceBetween size="l">
      <Box float="right">
        <AuditTrailExportButton auditTrail={auditTrail} evaluationId={evaluationId} />
      </Box>
      <PerformanceMetricsPanel entries={auditTrail.auditEntries} />
      <AuditTrailTimeline entries={auditTrail.auditEntries} />
    </SpaceBetween>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function EvaluationDetailPage() {
  const { evaluationId } = useParams<{ evaluationId: string }>();
  const client = useArbiterClient();
  const [evaluation, setEvaluation] = useState<EvaluationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [auditEnabled, setAuditEnabled] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTabId = searchParams.get('tab') || 'overview';
  useDocumentTitle(evaluation ? `Evaluation: ${formatSubject(evaluation)}` : 'Evaluation');

  // If arriving via ?tab=audit-trail, enable audit loading immediately
  useEffect(() => {
    if (activeTabId === 'audit-trail') {
      setAuditEnabled(true);
    }
  }, [activeTabId]);

  useEffect(() => {
    const fetchEvaluation = async () => {
      if (!evaluationId) return;

      try {
        setLoading(true);
        setError(null);
        const response = await client.describeEvaluation({ evaluationId });
        if (response.evaluation) setEvaluation(response.evaluation);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch evaluation'));
      } finally {
        setLoading(false);
      }
    };

    fetchEvaluation();
  }, [evaluationId, client]);

  const handleCancel = async () => {
    if (!evaluationId) return;

    setCancelling(true);
    try {
      await client.cancelEvaluation({ evaluationId });
      const response = await client.describeEvaluation({ evaluationId });
      if (response.evaluation) setEvaluation(response.evaluation);
      setShowCancelModal(false);
    } catch (err) {
      console.error('Failed to cancel evaluation:', err);
      setError(err instanceof Error ? err : new Error('Failed to cancel evaluation'));
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return <LoadingState label="Loading evaluation..." />;
  }

  if (error) {
    const is404 = error instanceof ResourceNotFoundException;
    return (
      <ErrorState
        variant={is404 ? 'not-found' : 'api-error'}
        title={is404 ? 'Evaluation not found' : 'Error loading evaluation'}
        message={is404
          ? `The evaluation "${evaluationId}" could not be found. It may have been deleted.`
          : error.message
        }
        onRetry={!is404 ? () => window.location.reload() : undefined}
        backLabel="Back to Evaluations"
        backHref="/evaluations"
      />
    );
  }

  if (!evaluation) {
    return (
      <ErrorState
        variant="not-found"
        title="Evaluation not found"
        message={`The evaluation "${evaluationId}" could not be found.`}
        backLabel="Back to Evaluations"
        backHref="/evaluations"
      />
    );
  }

  const canCancel = evaluation.status === EvaluationStatus.IN_PROGRESS;

  const headerLabel = (() => {
    const subject = formatSubject(evaluation);
    if (subject && subject !== UNKNOWN_SUBJECT) {
      return `Evaluation: ${subject}`;
    }
    return `Evaluation: ${evaluation.evaluationId?.slice(0, 8) ?? 'Unknown'}`;
  })();

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            {canCancel && (
              <Button onClick={() => setShowCancelModal(true)}>
                Cancel Evaluation
              </Button>
            )}
          </SpaceBetween>
        }
      >
        {headerLabel}
      </Header>

      <Tabs
        activeTabId={activeTabId}
        onChange={({ detail }) => {
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (detail.activeTabId === 'overview') {
              next.delete('tab');
            } else {
              next.set('tab', detail.activeTabId);
            }
            return next;
          }, { replace: true });
          if (detail.activeTabId === 'audit-trail') {
            setAuditEnabled(true);
          }
        }}
        tabs={[
          {
            id: 'overview',
            label: 'Overview',
            content: (
              <SpaceBetween size="l">
                <Container header={<Header variant="h2">Details</Header>}>
                  <ColumnLayout columns={2} variant="text-grid">
                    <KeyValuePairs
                      columns={1}
                      items={[
                        {
                          label: 'Evaluation ID',
                          value: evaluation.evaluationId,
                        },
                        {
                          label: 'Status',
                          value: (
                            <StatusIndicator type={getStatusType(evaluation.status!)}>
                              {EVALUATION_STATUS_DISPLAY[evaluation.status!]}
                            </StatusIndicator>
                          ),
                        },
                        ...(evaluation.subject ? [{
                          label: 'Subject',
                          value: formatSubject(evaluation),
                        }] : []),
                      ].filter(item => item.value != null && item.value !== '' && item.value !== '--')}
                    />
                    <KeyValuePairs
                      columns={1}
                      items={[
                        {
                          label: 'Bindle Lock',
                          value: evaluation.bindleLock,
                        },
                        {
                          label: 'Result',
                          value: evaluation.outcome === 'PASS' ? (
                            <StatusIndicator type="success">Pass</StatusIndicator>
                          ) : evaluation.outcome === 'FAIL' ? (
                            <StatusIndicator type="error">Fail</StatusIndicator>
                          ) : null,
                        },
                        ...(evaluation.snsTopic ? [{
                          label: 'SNS Topic',
                          value: evaluation.snsTopic,
                        }] : []),
                      ].filter(item => item.value != null)}
                    />
                  </ColumnLayout>

                  <Box margin={{ top: 'l' }}>
                    <KeyValuePairs
                      columns={3}
                      items={[
                        {
                          label: 'Start Time',
                          value: formatDateTime(evaluation.startTime),
                        },
                        ...(evaluation.endTime ? [{
                          label: 'End Time',
                          value: formatDateTime(evaluation.endTime),
                        }] : []),
                        ...(evaluation.executionTime != null ? [{
                          label: 'Execution Time',
                          value: `${evaluation.executionTime.toFixed(1)}s`,
                        }] : []),
                      ]}
                    />
                  </Box>
                </Container>

                {evaluation.results && evaluation.results.length > 0 && (

                  <Container header={
                    <Header
                      variant="h2"
                      info={
                        <Popover
                          header="Results count"
                          content="Rules evaluated may differ from total outcomes. A single rule can produce multiple findings, and some rules may be skipped based on applicability."
                          dismissButton={false}
                          position="top"
                          size="medium"
                          triggerType="text"
                        >
                          Info
                        </Popover>
                      }
                    >
                      Results ({evaluation.results.length})
                    </Header>
                  }>
                    <Table
                      columnDefinitions={[
                        {
                          id: 'ruleId',
                          header: 'Rule',
                          cell: (item: EvaluationResult) => {
                            if (!item.ruleId) return '--';
                            const { ruleName, version } = parseRuleId(item.ruleId);
                            if (!ruleName) return '--';
                            const ruleLink = (
                              <Link href={buildRuleUrl(ruleName, version)}>
                                {ruleName}
                              </Link>
                            );
                            if (item.description) {
                              return (
                                <>
                                  {ruleLink}{' '}
                                  <Popover
                                    dismissButton={false}
                                    position="top"
                                    size="medium"
                                    triggerType="text"
                                    content={item.description}
                                    renderWithPortal
                                  >
                                    Info
                                  </Popover>
                                </>
                              );
                            }
                            return ruleLink;
                          },
                        },
                        {
                          id: 'version',
                          header: 'Version',
                          cell: (item: EvaluationResult) => {
                            const { version } = parseRuleId(item.ruleId);
                            return version != null ? String(version) : '--';
                          },
                        },
                        {
                          id: 'outcome',
                          header: 'Outcome',
                          cell: (item: EvaluationResult) => {
                            const outcome = (item.outcome ?? 'INDETERMINATE') as EvaluationOutcome;
                            const labels: Record<EvaluationOutcome, string> = {
                              PASS: 'Pass', FAIL: 'Fail', ERROR: 'Error', INDETERMINATE: 'Indeterminate',
                            };
                            const badge = (
                              <StatusIndicator type={getOutcomeType(outcome)}>
                                {labels[outcome] ?? outcome}
                              </StatusIndicator>
                            );
                            if ((outcome === 'FAIL' || outcome === 'ERROR') && item.description) {
                              return (
                                <Popover
                                  dismissButton={false}
                                  position="top"
                                  size="large"
                                  triggerType="custom"
                                  content={<Box>{item.description}</Box>}
                                  renderWithPortal
                                >
                                  {badge}
                                </Popover>
                              );
                            }
                            return badge;
                          },
                        },
                        {
                          id: 'severity',
                          header: 'Severity',
                          cell: (item: EvaluationResult) => item.severity ?? '--',
                        },
                        {
                          id: 'confidence',
                          header: 'Confidence',
                          cell: (item: EvaluationResult) => <ConfidenceBadge confidence={item.confidence} />,
                        },
                      ]}
                      items={evaluation.results}
                      variant="embedded"
                      sortingDisabled
                    />
                  </Container>
                )}
              </SpaceBetween>
            ),
          },
          {
            id: 'audit-trail',
            label: 'Audit Trail',
            content: evaluationId ? (
              <AuditTrailTab evaluationId={evaluationId} enabled={auditEnabled} />
            ) : null,
          },
        ]}
      />

      <Modal
        visible={showCancelModal}
        onDismiss={() => setShowCancelModal(false)}
        header="Cancel Evaluation"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setShowCancelModal(false)}>
                Close
              </Button>
              <Button
                variant="primary"
                onClick={handleCancel}
                loading={cancelling}
                disabled={cancelling}
              >
                Cancel Evaluation
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        Are you sure you want to cancel this evaluation? This action cannot be undone.
      </Modal>
    </SpaceBetween>
  );
}
